import { KanbanUser } from '../types/kanban';

export class NotificationService {
  private static instance: NotificationService;
  private initialized: boolean = false;
  private teamId: string | null = null;
  private channelId: string | null = null;
  private tenantId: string | null = null;
  private boardId: string | null = null;

  private constructor() {}

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  public setInitialized(initialized: boolean) {
    this.initialized = initialized;
  }

  public setTeamsContext(teamId: string | null, channelId: string | null, tenantId: string | null = null, boardId: string | null = null) {
    this.teamId = teamId;
    this.channelId = channelId;
    this.tenantId = tenantId;
    this.boardId = boardId;
  }

  /**
   * Send notifications to assigned users (excluding custom users and the current user)
   */
  public async sendCardNotification(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actionType: 'updated' | 'commented on' | 'assigned you to' | 'moved' | 'added an attachment to',
    actorName: string,
    cardId?: string
  ): Promise<void> {
    if (!this.initialized) {
      console.warn('NotificationService not initialized');
      return;
    }

    try {
      // Filter to only non-custom users who are not the current user
      const usersToNotify = assignedUsers.filter(
        user => !user.isCustomUser && user.id !== currentUserId && user.id
      );

      console.log('Sending notification:', { assignedUsers, currentUserId, usersToNotify, actionType });

      if (usersToNotify.length === 0) {
        console.log('No users to notify');
        return;
      }

      const userIds = usersToNotify.map(user => user.id);
      console.log('Notifying user IDs:', userIds);

      // Backend now uses application permissions (no user token needed)
      // Pass tenantId so backend can authenticate against the correct tenant
      const functionEndpoint = process.env.REACT_APP_FUNC_ENDPOINT || '';
      const response = await fetch(`${functionEndpoint}/notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userIds,
          cardTitle,
          actionType,
          actorName,
          teamId: this.teamId,
          channelId: this.channelId,
          tenantId: this.tenantId,
          cardId,
          boardId: this.boardId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to send notifications:', errorData);
        console.error('Response status:', response.status);
      } else {
        const result = await response.json();
        console.log('Notifications sent successfully:', result);

        // Check if any failed
        const failed = result.results?.filter((r: any) => !r.success);
        if (failed && failed.length > 0) {
          console.error('Some notifications failed:', failed);
        }
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
    }
  }

  /**
   * Notify users when they are assigned to a card
   */
  public async notifyAssignment(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actorName: string,
    cardId?: string
  ): Promise<void> {
    await this.sendCardNotification(
      assignedUsers,
      currentUserId,
      cardTitle,
      'assigned you to',
      actorName,
      cardId
    );
  }

  /**
   * Notify users when a card is updated
   */
  public async notifyUpdate(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actorName: string,
    cardId?: string
  ): Promise<void> {
    await this.sendCardNotification(
      assignedUsers,
      currentUserId,
      cardTitle,
      'updated',
      actorName,
      cardId
    );
  }

  /**
   * Notify users when a comment is added
   */
  public async notifyComment(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actorName: string,
    cardId?: string
  ): Promise<void> {
    await this.sendCardNotification(
      assignedUsers,
      currentUserId,
      cardTitle,
      'commented on',
      actorName,
      cardId
    );
  }

  /**
   * Notify users when a card is moved
   */
  public async notifyMove(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actorName: string,
    cardId?: string
  ): Promise<void> {
    await this.sendCardNotification(
      assignedUsers,
      currentUserId,
      cardTitle,
      'moved',
      actorName,
      cardId
    );
  }

  /**
   * Notify users when an attachment is added
   */
  public async notifyAttachment(
    assignedUsers: KanbanUser[],
    currentUserId: string,
    cardTitle: string,
    actorName: string,
    cardId?: string
  ): Promise<void> {
    await this.sendCardNotification(
      assignedUsers,
      currentUserId,
      cardTitle,
      'added an attachment to',
      actorName,
      cardId
    );
  }
}

export const notificationService = NotificationService.getInstance();
